import { useEffect, useState } from "react";
import { Users, KeyRound, Loader2, Check, UserPlus, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import ProfileDialog from "./ProfileDialog";
import { useAuth } from "../lib/auth";
import { isOwner } from "../lib/roles";
import {
  listUsers,
  adminResetPassword,
  usersMeta,
  createUser,
  updateUserPermissions,
} from "../lib/api";

const ROLE_BADGE = {
  admin: "bg-blue-100 text-blue-700",
  owner: "bg-violet-100 text-violet-700",
  dispatcher: "bg-emerald-100 text-emerald-700",
  truck: "bg-amber-100 text-amber-700",
};

const ROLE_LABEL = {
  admin: "Office",
  owner: "Owner",
  dispatcher: "Dispatcher",
  truck: "Driver",
};

function AddPersonForm({ presets, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [preset, setPreset] = useState("dispatcher");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPw(""); setPreset("dispatcher");
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Enter their name"); return; }
    if (!email.trim()) { toast.error("Enter their email"); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    const chosen = presets.find((p) => p.key === preset);
    if (!chosen) {
      toast.error("Couldn't load the starting points — reload the page and try again");
      return;
    }
    setSaving(true);
    try {
      const created = await createUser({
        name: name.trim(),
        email: email.trim(),
        password: pw,
        role: chosen.role,
        permissions: chosen.permissions,
      });
      toast.success(`${created.name} can now sign in`);
      reset();
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create that login");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!presets.length}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        data-testid="add-user-open"
      >
        <UserPlus className="h-4 w-4" /> Add a person
      </Button>
    );
  }

  return (
    <div
      className="border border-slate-300 dark:border-slate-600 rounded-md p-4 space-y-3 bg-slate-50 dark:bg-slate-800/50"
      data-testid="add-user-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          data-testid="add-user-name"
        />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email they'll sign in with"
          data-testid="add-user-email"
        />
        <Input
          type="text"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Starting password (min 8 chars)"
          data-testid="add-user-password"
        />
        <select
          value={preset}
          onChange={(e) => setPreset(e.target.value)}
          className="h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm"
          data-testid="add-user-preset"
        >
          {presets.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Tell them this password yourself — we don't email it. You can change
        what they're allowed to do straight after.
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="add-user-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Create login
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => { reset(); setOpen(false); }}
          data-testid="add-user-cancel"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function PermissionSwitches({ u, capabilities, onSaved }) {
  const [busy, setBusy] = useState(null);
  const perms = u.permissions || {};

  const flick = async (key, value) => {
    setBusy(key);
    try {
      const res = await updateUserPermissions(u.id, { [key]: value });
      onSaved(u.id, res.permissions);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-slate-200 dark:border-slate-700 pt-3" data-testid={`user-perms-${u.id}`}>
      {capabilities.map((c) => (
        <div key={c.key} className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.label}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{c.help}</div>
          </div>
          <div className="shrink-0 pt-0.5">
            {busy === c.key ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : (
              <Switch
                checked={perms[c.key] === true}
                onCheckedChange={(v) => flick(c.key, v)}
                data-testid={`user-perm-${u.id}-${c.key}`}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserRow({ u, capabilities, canEditAccess, canResetPassword, canEditDetails, onSaved, onProfileSaved }) {
  const [open, setOpen] = useState(false);
  const [showPerms, setShowPerms] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await adminResetPassword(u.id, pw);
      toast.success(`Password updated for ${u.email}`);
      setPw(""); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update password");
    } finally {
      setSaving(false);
    }
  };

  const isOwnerRow = u.role === "owner";

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3" data-testid={`user-row-${u.id}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{u.name || "—"}</div>
          <div className="text-xs text-slate-500 break-all sm:truncate">{u.email}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${ROLE_BADGE[u.role] || "bg-slate-100 text-slate-600"}`}>
            {ROLE_LABEL[u.role] || u.role}
          </span>
          {canEditDetails && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid={`user-edit-toggle-${u.id}`}
            >
              <Pencil className="h-3.5 w-3.5" /> Name &amp; email
            </Button>
          )}
          {canEditAccess && !isOwnerRow && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPerms((s) => !s)}
              data-testid={`user-access-toggle-${u.id}`}
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Access
            </Button>
          )}
          {canResetPassword && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen((o) => !o)}
              data-testid={`user-reset-toggle-${u.id}`}
            >
              <KeyRound className="h-3.5 w-3.5" /> Reset password
            </Button>
          )}
        </div>
      </div>
      {isOwnerRow && (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          The owner always has full access.
        </div>
      )}
      {open && (
        <div className="mt-3 flex items-center gap-2" data-testid={`user-reset-form-${u.id}`}>
          <Input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password (min 8 chars)"
            className="flex-1"
            data-testid={`user-reset-input-${u.id}`}
          />
          <Button type="button" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`user-reset-save-${u.id}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </Button>
        </div>
      )}
      {showPerms && (
        <PermissionSwitches u={u} capabilities={capabilities} onSaved={onSaved} />
      )}
      <ProfileDialog
        person={u}
        open={editing}
        onOpenChange={setEditing}
        onSaved={onProfileSaved}
      />
    </div>
  );
}

export default function UserManagementCard() {
  const { user, refreshUser } = useAuth();
  const owner = isOwner(user);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ capabilities: [], presets: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    listUsers()
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Couldn't load users"))
      .finally(() => setLoading(false));
  };

  // Keep the saved answer in the list, not inside the switch panel — closing
  // and reopening Access used to show the pre-change values.
  const onSaved = (id, permissions) =>
    setUsers((list) => list.map((u) => (u.id === id ? { ...u, permissions } : u)));

  useEffect(() => {
    load();
    usersMeta()
      .then(setMeta)
      .catch(() => toast.error("Couldn't load the access switches — reload the page"));
  }, []);

  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6" data-testid="user-management-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">Users &amp; Access</h2>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        {owner
          ? "Add your team, set their password, and decide what each of them can do."
          : "Reset any team member's password here. Only the owner can add people or change what they're allowed to do."}
      </p>
      {owner && (
        <div className="mb-4">
          <AddPersonForm presets={meta.presets || []} onCreated={load} />
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading users…</div>
      ) : users.length === 0 ? (
        <div className="text-sm text-slate-500">No users found.</div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <UserRow
              key={u.id}
              u={u}
              capabilities={meta.capabilities || []}
              canEditAccess={owner}
              // An Office user must not be able to set the OWNER's password —
              // that was a way to become the owner and switch your own
              // permissions on. The API refuses it too.
              canResetPassword={owner || u.role !== "owner"}
              // Anyone may fix their own; the owner may fix anybody's — which
              // is the only way back for someone who mistyped their email.
              canEditDetails={owner || u.id === user?.id}
              onSaved={onSaved}
              onProfileSaved={(res) => {
                setUsers((list) => list.map((x) =>
                  x.id === res.id ? { ...x, name: res.name, email: res.email } : x));
                if (res.id === user?.id) refreshUser?.();
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
